import { useQuery } from '@tanstack/react-query';
import { Col, Row, Skeleton, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  REVENUE_STREAMS,
  fetchBikesSummary,
  fetchOperations,
  fetchPartsSummary,
  fetchPayablesSummary,
  fetchPurchasesSummary,
  fetchReceivablesSummary,
  fetchSalesSummary,
  type LedgerSummary,
  type SalesSummary,
} from './api';
import { formatPercent, formatUnits, monthName, sumMoney, toSen } from './figures';
import { Tile } from './Tile';

const { Text } = Typography;

export type InsightsTab = 'overview' | 'operations' | 'exceptions' | 'sales' | 'bikes' | 'parts' | 'receivables' | 'payables' | 'purchasing';

interface Shown {
  value: string;
  lines: string[];
  /** The source's own "as of" line. */
  asOf: string;
}

type Source<T> = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: T };

/** Gross profit over the revenue that carries a cost, to a tenth of a percent. */
function grossProfitRatio(data: SalesSummary): number | null {
  const costed = toSen(
    sumMoney(data.months.flatMap((m) => REVENUE_STREAMS.map((s) => m.byStream[s].costedRevenue))),
  );
  if (costed <= 0n) return null;
  return Number((toSen(data.totals.grossProfit) * 10_000n) / costed) / 10_000;
}

/**
 * Insights, Overview: one tile per source, each with its own "as of", each a
 * way into that source's tab. A source that is loading, failed or never read
 * says so on its own tile and leaves the others alone.
 */
export function OverviewTab({ year, onOpen }: { year: number; onOpen: (tab: InsightsTab) => void }): ReactNode {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const sales = useQuery({ queryKey: ['analytics', 'sales', 'summary', year], queryFn: () => fetchSalesSummary(year) });
  const bikes = useQuery({ queryKey: ['analytics', 'bikes', 'summary', year], queryFn: () => fetchBikesSummary(year) });
  const parts = useQuery({ queryKey: ['analytics', 'parts', 'summary', year], queryFn: () => fetchPartsSummary(year) });
  const purchases = useQuery({
    queryKey: ['analytics', 'purchases', 'summary', year],
    queryFn: () => fetchPurchasesSummary(year),
  });
  const receivables = useQuery({ queryKey: ['analytics', 'receivables', 'summary'], queryFn: fetchReceivablesSummary });
  const payables = useQuery({ queryKey: ['analytics', 'payables', 'summary'], queryFn: fetchPayablesSummary });
  const operations = useQuery({ queryKey: ['analytics', 'operations', null], queryFn: () => fetchOperations(null) });

  const source = <T,>(q: { data: T | undefined; isError: boolean }): Source<T> =>
    q.isError ? { status: 'error' } : q.data === undefined ? { status: 'loading' } : { status: 'ready', data: q.data };

  const dataAsOf = (at: string): string => t('insights.dataAsOf', { at: formatDateTime(at) });
  const asRead = (at: string): string => t('insights.asRead', { at: formatDateTime(at) });
  const range = (through: number): string => {
    const lastYear = String(year - 1);
    return through === 1
      ? t('insights.sales.vsMonth', { from: monthName(1, language), year: lastYear })
      : t('insights.sales.vsRange', { from: monthName(1, language), to: monthName(through, language), year: lastYear });
  };
  const over90 = (data: LedgerSummary): string =>
    t('insights.overview.over90', {
      amount: formatMoney(data.buckets.find((b) => b.bucket === 'D90_PLUS')?.amount ?? '0.00'),
    });

  const tile = <T,>(
    id: string,
    label: string,
    tab: InsightsTab,
    src: Source<T>,
    show: (data: T) => Shown | null,
  ): ReactNode => {
    let body: ReactNode;
    let value = '';
    if (src.status === 'loading') {
      body = <Skeleton active title={false} paragraph={{ rows: 2 }} />;
    } else if (src.status === 'error') {
      body = <Text type="danger">{t('insights.loadFailed')}</Text>;
    } else {
      const shown = show(src.data);
      if (shown === null) {
        value = t('insights.overview.notRead');
      } else {
        value = shown.value;
        body = (
          <>
            {shown.lines.map((line) => (
              <Text key={line} type="secondary">
                {line}
              </Text>
            ))}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {shown.asOf}
            </Text>
          </>
        );
      }
    }
    return (
      <Col xs={24} sm={12} xl={6}>
        <div
          role="link"
          tabIndex={0}
          aria-label={label}
          style={{ cursor: 'pointer', height: '100%' }}
          onClick={() => {
            onOpen(tab);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(tab);
            }
          }}
        >
          <Tile id={id} label={label} value={value}>
            {body}
          </Tile>
        </div>
      </Col>
    );
  };

  const ledgerTile = (id: string, key: 'receivables' | 'payables', tab: InsightsTab, q: typeof receivables): ReactNode =>
    tile(id, t(`insights.overview.${key}`), tab, source(q), (d) =>
      d.dataAsOf === null ? null : { value: formatMoney(d.owed), lines: [over90(d)], asOf: asRead(d.dataAsOf) },
    );

  return (
    <Row gutter={[12, 12]}>
      {tile('ov-operations', t('insights.overview.operations'), 'operations', source(operations), (d) => {
        // Work waiting too long: late leads (each once, though a lead can be
        // late on both counts), registration past target, job cards open over
        // 3 days, documents not handed over, cases past their times. Comebacks
        // are a quality figure, not waiting work, so they stay on Operations.
        const late =
          d.sales.leadsLate +
          d.registration.reduce((sum, g) => sum + g.overdue, 0) +
          d.workshop.openOver3Days +
          d.till.notHandedOver +
          d.service.casesLate;
        return {
          value: String(late),
          lines: [t('insights.overview.operationsLine', { jobs: d.workshop.open, bookings: d.sales.bookingsOpen })],
          asOf: t('insights.operations.countedAt', { at: formatDateTime(d.asOf) }),
        };
      })}
      {tile('ov-sales', t('insights.overview.sales'), 'sales', source(sales), (d) => {
        if (d.dataAsOf === null) return null;
        const ratio = grossProfitRatio(d);
        const lines = [
          t('insights.overview.salesVsLastYear', {
            range: range(d.totals.comparedThroughMonth),
            amount: formatMoney(d.totals.lastYearRevenue),
          }),
        ];
        if (ratio !== null) lines.push(t('insights.overview.grossProfit', { percent: formatPercent(ratio, language) }));
        return { value: formatMoney(d.totals.revenue), lines, asOf: dataAsOf(d.dataAsOf) };
      })}
      {tile('ov-bikes-sold', t('insights.overview.bikesSold'), 'bikes', source(bikes), (d) =>
        d.salesDataAsOf === null
          ? null
          : {
              value: formatUnits(d.unitsThisYear),
              lines: [
                t('insights.overview.bikesSoldVsLastYear', {
                  range: range(d.comparedThroughMonth),
                  units: formatUnits(d.unitsLastYearSamePeriod),
                }),
              ],
              asOf: dataAsOf(d.salesDataAsOf),
            },
      )}
      {ledgerTile('ov-receivables', 'receivables', 'receivables', receivables)}
      {ledgerTile('ov-payables', 'payables', 'payables', payables)}
      {tile('ov-parts', t('insights.overview.parts'), 'parts', source(parts), (d) =>
        d.stockDataAsOf === null ? null : { value: formatMoney(d.stockValue), lines: [], asOf: asRead(d.stockDataAsOf) },
      )}
      {tile('ov-unsold', t('insights.overview.unsold'), 'bikes', source(bikes), (d) =>
        d.stockDataAsOf === null
          ? null
          : {
              value: String(d.stock.bikes),
              lines: [t('insights.overview.unsoldCost', { amount: formatMoney(d.stock.cost) })],
              asOf: asRead(d.stockDataAsOf),
            },
      )}
      {tile('ov-purchases', t('insights.overview.purchases'), 'purchasing', source(purchases), (d) =>
        d.dataAsOf === null
          ? null
          : {
              value: formatMoney(d.thisYear),
              lines: [
                t('insights.overview.purchasesVsLastYear', {
                  range: range(d.comparedThroughMonth),
                  amount: formatMoney(d.sameMonthsLastYear),
                }),
              ],
              asOf: dataAsOf(d.dataAsOf),
            },
      )}
    </Row>
  );
}
